import { Link, useLocation } from "react-router";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Package,
  BarChart,
  Cog,
  Home,
  ShoppingCart,
  Users,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  Tag,
  Store,
  Settings,
  IndianRupee,
  Activity,
  HandCoins,
  PackageSearch,
  Building2
} from "lucide-react";
import { useState, useEffect, useContext } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AuthContext } from "@/context/AuthContext";

interface SidebarNavProps {
  onLogout: () => void;
  isCollapsed: boolean;
}

export function SidebarNav({ onLogout, isCollapsed }: SidebarNavProps) {
  const location = useLocation();
    const { currentUser } = useContext(AuthContext);
  const [transactionOpen, setTransactionOpen] = useState(
    location.pathname.includes('/transactions')
  );
  const [manageOpen, setManageOpen] = useState(
    location.pathname.includes('/manage')
  );
  const [analyticsOpen, setAnalyticsOpen] = useState(
    location.pathname.includes('/analytics')
  );

  // Close all dropdowns when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed) {
      setTransactionOpen(false);
      setManageOpen(false);
      setAnalyticsOpen(false);
    }
  }, [isCollapsed]);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const sidebarItems = [
    { text: "Dashboard", icon: <Home size={20} />, path: "/dashboard" },
    //{ text: "Inventory", icon: <Package size={20} />, path: "/inventory" },
  ];

  const transactionItems = [
    { text: "Sale", icon: <IndianRupee size={20} />, path: "/transactions/sale" },
    { text: "Purchase", icon: <ShoppingBag size={20} />, path: "/transactions/purchase" },
  ];

  const analyticsItems = [
    { text: "Purchase Report", icon: <ShoppingBag size={20} />, path: "/analytics/purchase" },
    { text: "Sale Report", icon: <IndianRupee size={20} />, path: "/analytics/sale" },
    { text: "Stock Report", icon: <Activity size={20} />, path: "/analytics/stock" },
    { text: "Profit Report", icon: <HandCoins size={20} />, path: "/analytics/profit" },
  ];

  const manageItems = [
    { text: "Categories", icon: <Tag size={20} />, path: "/manage/categories" },
    { text: "Products", icon: <PackageSearch size={20} />, path: "/manage/products" },
    { text: "Users", icon: <Users size={20} />, path: "/manage/users" },
    { text: "Vendors", icon: <Building2 size={20} />, path: "/manage/vendors" },
  ];

  const otherItems = [
    { text: "Settings", icon: <Settings size={20} />, path: "/settings" },
  ];

  // Check if user is admin or super admin
  const isAdminOrSuperAdmin = currentUser?.role === 'Admin' || currentUser?.role === 'SuperAdmin';

  // Render a nav item with tooltip when collapsed
  const renderNavItem = (item: { text: string; icon: JSX.Element; path: string }) => {
    const navItem = (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive(item.path)
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }`}
      >
        {item.icon}
        {!isCollapsed && <span>{item.text}</span>}
      </Link>
    );

    if (isCollapsed) {
      return (
        <TooltipProvider key={item.text}>
          <Tooltip>
            <TooltipTrigger asChild>
              {navItem}
            </TooltipTrigger>
            <TooltipContent side="right">
              {item.text}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={item.text}>{navItem}</div>;
  };

  return (
    <>
      <div className={`p-6 ${isCollapsed ? 'flex justify-center p-4' : ''}`}>
        <Link to="/dashboard" className={`flex items-center gap-2 font-bold ${isCollapsed ? '' : 'text-xl'}`}>
          <Store size={24} className="text-primary" />
          {!isCollapsed && <span>J M Store</span>}
        </Link>
      </div>
      <Separator />
      <div className="flex-1 py-4 overflow-y-auto">
        <nav className="px-2 space-y-1">
          {sidebarItems.map(item => renderNavItem(item))}

          {/* Transactions Collapsible Menu */}
          {isCollapsed ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/transactions/sale"
                    className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/transactions')
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    <ShoppingCart size={20} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Transactions
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Collapsible
              open={transactionOpen}
              onOpenChange={setTransactionOpen}
              className="w-full"
            >
              <CollapsibleTrigger asChild>
                <button
                  className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/transactions')
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <ShoppingCart size={20} />
                    <span>Transactions</span>
                  </div>
                  {transactionOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-9 pr-2 py-1 space-y-1">
                {transactionItems.map((item) => (
                  <Link
                    key={item.text}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${location.pathname === item.path
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    {item.icon}
                    {item.text}
                  </Link>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Analytics Collapsible Menu */}
          {isCollapsed ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/analytics/purchase"
                    className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/analytics')
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    <BarChart size={20} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Analytics
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Collapsible
              open={analyticsOpen}
              onOpenChange={setAnalyticsOpen}
              className="w-full"
            >
              <CollapsibleTrigger asChild>
                <button
                  className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/analytics')
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <BarChart size={20} />
                    <span>Analytics</span>
                  </div>
                  {analyticsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-9 pr-2 py-1 space-y-1">
                {analyticsItems.map((item) => (
                  <Link
                    key={item.text}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${location.pathname === item.path
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    {item.icon}
                    {item.text}
                  </Link>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Manage Menu - Only visible to Admin and SuperAdmin */}
          {isAdminOrSuperAdmin && (
            isCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/manage/users"
                      className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/manage')
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                    >
                      <Cog size={20} />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Manage
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Collapsible
                open={manageOpen}
                onOpenChange={setManageOpen}
                className="w-full"
              >
                <CollapsibleTrigger asChild>
                  <button
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${location.pathname.includes('/manage')
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <Cog size={20} />
                      <span>Manage</span>
                    </div>
                    {manageOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-9 pr-2 py-1 space-y-1">
                  {manageItems.map((item) => (
                    <Link
                      key={item.text}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${location.pathname === item.path
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                    >
                      {item.icon}
                      {item.text}
                    </Link>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )
          )}

          {otherItems.map(item => renderNavItem(item))}
        </nav>
      </div>
    </>
  );
}